/**
 * "Zeilen schreiben" for one target table: whether a run adds a new row,
 * updates an existing one, or updates it and adds it when it is missing.
 * Updating needs to recognise the row: one or more columns whose value is
 * compared with the response field that is mapped to them.
 *
 * The Files table never gets new rows from an automation. A table that is
 * also the source table can take the result in the row a run is processing
 * (no recognising needed) and says what the other choices mean for that row.
 */
import { useI18n } from '@affine/i18n';
import {
  EditIcon,
  InsertBelowIcon,
  ReplaceIcon,
  ResetIcon,
} from '@blocksuite/icons/rc';
import type { FC } from 'react';

import type {
  MatchConfigProps,
  MultiMatchBehavior,
  OutputMapping,
  OutputTable,
  TargetMatchMode,
} from '../../services/automation';
import type { ColumnInfo } from '../../services/database-discovery';
import { isFileIndexTable } from './file-index-table';
import {
  MATCH_MODE_WITH_AUTO_OPTIONS,
  MULTI_MATCH_BEHAVIOR_OPTIONS,
  useMatchOptions,
} from './match-config-editor';
import {
  removeRowKey,
  rowKeyCandidates,
  rowMatchConfigOf,
  type RowWriteMode,
  rowWriteModeOf,
  type RowWriteState,
  setRowKey,
  setRowKeyMatchMode,
  setRowMultiMatch,
  setRowWriteMode,
} from './row-write';
import {
  SectionAddLink,
  SectionField,
  SectionHint,
  SectionRemoveButton,
  SectionStack,
  SectionTableRow,
} from './section-layout';
import { SettingSelect, type SettingSelectOption } from './setting-select';

export interface RowWriteSectionProps {
  table: OutputTable;
  /** The table's columns. */
  columns: readonly ColumnInfo[];
  /** The automation's output mappings (all tables). */
  mappings: readonly OutputMapping[];
  matchConfigs: MatchConfigProps[];
  outputTables: OutputTable[];
  /** The table is also the table the processed rows come from. */
  isSourceTable: boolean;
  onChange: (state: RowWriteState) => void;
}

export const RowWriteSection: FC<RowWriteSectionProps> = ({
  table,
  columns,
  mappings,
  matchConfigs,
  outputTables,
  isSourceTable,
  onChange,
}) => {
  const t = useI18n();
  const compareOptions = useMatchOptions(MATCH_MODE_WITH_AUTO_OPTIONS);
  const multipleOptions = useMatchOptions(MULTI_MATCH_BEHAVIOR_OPTIONS);

  const tableId = table.tableId;
  const state: RowWriteState = { matchConfigs, outputTables };
  const matchConfig = rowMatchConfigOf(state, tableId);
  const mode = rowWriteModeOf(
    matchConfig,
    isSourceTable && table.mode === 'update_source'
  );
  const candidates = rowKeyCandidates(tableId, mappings);
  const filesTable = isFileIndexTable(tableId, columns);

  const needsMapping =
    candidates.length === 0
      ? t['com.streamlain.automation.sidebar.write.needs-mapping']()
      : undefined;
  const noNewRows = filesTable
    ? t['com.streamlain.automation.sidebar.write.files-table']()
    : undefined;
  const modeOptions: SettingSelectOption<RowWriteMode>[] = [
    ...(isSourceTable
      ? [
          {
            value: 'source' as const,
            icon: <ResetIcon />,
            label: t['com.streamlain.automation.sidebar.write.source'](),
            description:
              t['com.streamlain.automation.sidebar.write.source-desc'](),
          },
        ]
      : []),
    {
      value: 'append',
      icon: <InsertBelowIcon />,
      label: t['com.streamlain.automation.sidebar.write.append'](),
      description:
        noNewRows ?? t['com.streamlain.automation.sidebar.write.append-desc'](),
      disabled: !!noNewRows,
    },
    {
      value: 'update',
      icon: <EditIcon />,
      label: t['com.streamlain.automation.sidebar.write.update'](),
      description:
        needsMapping ??
        t['com.streamlain.automation.sidebar.write.update-desc'](),
      disabled: !!needsMapping,
    },
    {
      value: 'upsert',
      icon: <ReplaceIcon />,
      label: t['com.streamlain.automation.sidebar.write.upsert'](),
      description:
        noNewRows ??
        needsMapping ??
        t['com.streamlain.automation.sidebar.write.upsert-desc'](),
      disabled: !!noNewRows || !!needsMapping,
    },
  ];

  const columnName = (columnId: string) =>
    columns.find(column => column.id === columnId)?.name ?? columnId;
  const fieldName = (columnId: string) => {
    const mapping = mappings.find(
      entry => entry.tableId === tableId && entry.targetColumnId === columnId
    );
    return mapping?.responseFieldName || mapping?.responseField || '';
  };
  const keys = matchConfig?.keys ?? [];
  const usedColumns = new Set(keys.map(key => key.columnId));
  const freeCandidate = candidates.find(
    candidate => !usedColumns.has(candidate.columnId)
  );
  const keyOptionsOf = (index: number): SettingSelectOption<string>[] => {
    const own = keys[index]?.columnId;
    return candidates.map(candidate => ({
      value: candidate.columnId,
      label: columnName(candidate.columnId),
      description: t['com.streamlain.automation.sidebar.write.key-option']({
        field: fieldName(candidate.columnId),
      }),
      disabled:
        candidate.columnId !== own && usedColumns.has(candidate.columnId),
    }));
  };

  const modeLabel = t['com.streamlain.automation.sidebar.write.mode-label']();
  const keyLabel = t['com.streamlain.automation.sidebar.write.key-label']();
  const compareLabel =
    t['com.streamlain.automation.sidebar.write.compare-label']();
  const multipleLabel =
    t['com.streamlain.automation.sidebar.write.multiple-label']();

  return (
    <SectionStack>
      <SectionField label={modeLabel}>
        <SettingSelect
          value={mode}
          options={modeOptions}
          onChange={next =>
            onChange(setRowWriteMode(state, tableId, next, candidates))
          }
          ariaLabel={modeLabel}
          data-testid="row-write-mode"
        />
      </SectionField>

      {mode === 'append' && filesTable && (
        <SectionHint tone="warning">
          {t['com.streamlain.automation.sidebar.write.files-table-warning']()}
        </SectionHint>
      )}
      {isSourceTable && mode !== 'source' && (
        <SectionHint tone={mode === 'append' ? 'warning' : 'neutral'}>
          {mode === 'append'
            ? t['com.streamlain.automation.sidebar.write.source-append']()
            : t['com.streamlain.automation.sidebar.write.source-update']()}
        </SectionHint>
      )}

      {(mode === 'update' || mode === 'upsert') && (
        <>
          <SectionField
            label={keyLabel}
            hint={t['com.streamlain.automation.sidebar.write.key-hint']()}
          >
            {keys.map((key, index) => (
              <SectionStack key={key.columnId} tight>
                <SectionTableRow
                  action={
                    keys.length > 1 ? (
                      <SectionRemoveButton
                        onClick={() =>
                          onChange(removeRowKey(state, tableId, index))
                        }
                        title={t[
                          'com.streamlain.automation.sidebar.write.key-remove'
                        ]()}
                      />
                    ) : undefined
                  }
                >
                  <SettingSelect
                    value={key.columnId}
                    options={keyOptionsOf(index)}
                    onChange={columnId => {
                      const candidate = candidates.find(
                        entry => entry.columnId === columnId
                      );
                      if (candidate) {
                        onChange(setRowKey(state, tableId, index, candidate));
                      }
                    }}
                    placeholder={columnName(key.columnId)}
                    ariaLabel={keyLabel}
                    data-testid="row-write-key"
                  />
                </SectionTableRow>
                <SectionField label={compareLabel}>
                  <SettingSelect<TargetMatchMode>
                    value={key.matchMode as TargetMatchMode}
                    options={compareOptions}
                    onChange={matchMode =>
                      onChange(
                        setRowKeyMatchMode(state, tableId, index, matchMode)
                      )
                    }
                    ariaLabel={`${compareLabel}: ${columnName(key.columnId)}`}
                  />
                </SectionField>
              </SectionStack>
            ))}
            {freeCandidate && (
              <SectionAddLink
                onClick={() =>
                  onChange(
                    setRowKey(state, tableId, keys.length, freeCandidate)
                  )
                }
              >
                <span>+</span>
                {t['com.streamlain.automation.sidebar.write.key-add']()}
              </SectionAddLink>
            )}
          </SectionField>
          <SectionField label={multipleLabel}>
            <SettingSelect<MultiMatchBehavior>
              value={matchConfig?.multiMatchBehavior ?? 'fail'}
              options={multipleOptions}
              onChange={behavior =>
                onChange(setRowMultiMatch(state, tableId, behavior))
              }
              ariaLabel={multipleLabel}
            />
          </SectionField>
        </>
      )}
    </SectionStack>
  );
};
